/**
 * Merges a preview overlay onto a store object, producing the `CrmObject` the
 * record will look like once the overlay is applied.
 *
 * This extends `buildProjectedObject` — the same projection the proposal
 * draft surface uses — rather than reimplementing merge semantics: a
 * `create` overlay (no store object) produces a synthetic record; an
 * `update` overlay merges overlay-wins per property on top of the store
 * object, leaving un-overlaid keys at their store values, and concatenates
 * association overlays onto the store object's associations.
 *
 * `mergeProjection(undefined, updateOverlay, orgId)` — an `update` overlay
 * matched against a store object that has not been fetched yet — still
 * returns a synthetic partial record carrying only the overlay's fields,
 * because `buildProjectedObject` treats a missing base as "creating". That
 * partial renders until the store fetch resolves and this is called again
 * with the real object, so callers that render an update overlay should gate
 * on their store hook's `isLoading` where they need to distinguish "loading"
 * from "genuinely empty".
 */

import type { ProjectedRecord } from '@/lib/crm/record/buildProjectedObject';
import { buildProjectedObject } from '@/lib/crm/record/buildProjectedObject';
import type { CrmObject } from '@/types/crm';

import type { PreviewOverlay } from './types';

export function projectOverlay(
  storeObject: CrmObject | undefined,
  overlay: PreviewOverlay,
  orgId: number
): ProjectedRecord | undefined {
  return buildProjectedObject({
    base: storeObject,
    overlay: overlay.properties,
    entityTypeName: overlay.entityTypeName,
    entityId: overlay.entityId,
    orgId,
    associationOverlays: overlay.associations?.map(a => ({
      association_type_name: a.association_type_name,
      target_entity_id: a.target_entity_id,
    })),
    removedAssociationOverlays: overlay.removedAssociations?.map(a => ({
      association_type_name: a.association_type_name,
      target_entity_id: a.target_entity_id,
    })),
  });
}

export function mergeProjection(
  storeObject: CrmObject | undefined,
  overlay: PreviewOverlay,
  orgId: number
): CrmObject | undefined {
  // `buildProjectedObject` only returns undefined when it can't resolve a
  // type name — never the case here since a `PreviewOverlay` always carries
  // `entityTypeName`.
  return projectOverlay(storeObject, overlay, orgId)?.projectedObject;
}
